import { BreadcrumbAgency } from '@/components/breadcrumb/BreadcrumbAgency.tsx';
import { SidebarAgency } from '@/components/sidebar/SidebarAgency.tsx';
import { menuTabs } from '@/components/sidebar/sidebarTabsAgency.tsx';
import { AccountFormAgency } from '@/pages/account/AccountFormAgency.tsx';
import { AccountListAgency } from '@/pages/account/AccountListAgency.tsx';
import { AdGroupFormAgency } from '@/pages/ad-group/AdGroupFormAgency.tsx';
import { AdGroupListAgency } from '@/pages/ad-group/AdGroupListAgency.tsx';
import AdUnitForm from '@/pages/ad-unit/AdUnitForm.tsx';
import AdUnitList from '@/pages/ad-unit/AdUnitList.tsx';
import AdvertiserForm from '@/pages/advertiser/AdvertiserForm';
import AdvertiserList from '@/pages/advertiser/AdvertiserList';
import { Branding } from '@/pages/branding/Branding.tsx';
import { StripeConnectSettings } from '@/pages/stripe-connect/StripeConnectSettings.tsx';
import { CampaignFormAgency } from '@/pages/campaign/CampaignFormAgency.tsx';
import { CampaignListAgency } from '@/pages/campaign/CampaignListAgency.tsx';
import { CreativeFormAgency } from '@/pages/creative/CreativeFormAgency.tsx';
import { CreativeListAgency } from '@/pages/creative/CreativeListAgency.tsx';
import { DashboardFormAgency } from '@/pages/dashboard/DashboardFormAgency.tsx';
import DealForm from '@/pages/deal/DealForm.tsx';
import DealList from '@/pages/deal/DealList.tsx';
import DspForm from '@/pages/dsp/DspForm.tsx';
import DspList from '@/pages/dsp/DspList.tsx';
import { FilterListGlobalFormAgency } from '@/pages/filter-list-global/FilterListGlobalFormAgency.tsx';
import { FilterListGlobalListAgency } from '@/pages/filter-list-global/FilterListGlobalListAgency.tsx';
import { FilterListFormAgency } from '@/pages/filter-list/FilterListFormAgency.tsx';
import { FilterListListAgency } from '@/pages/filter-list/FilterListListAgency.tsx';
import { LogListAgency } from '@/pages/log/LogListAgency.tsx';
import NbrDebug from '@/pages/nbr-debug/NbrDebug.tsx';
import PublisherForm from '@/pages/publisher/PublisherForm.tsx';
import PublisherList from '@/pages/publisher/PublisherList.tsx';
import { ReportFormAgency } from '@/pages/report/ReportFormAgency.tsx';
import { SavedReportListAgency } from '@/pages/report/SavedReportListAgency.tsx';
import { RetargetingListFormAgency } from '@/pages/retargeting-list/RetargetingListFormAgency.tsx';
import { RetargetingListListAgency } from '@/pages/retargeting-list/RetargetingListListAgency.tsx';
import SiteForm from '@/pages/site/SiteForm.tsx';
import SiteList from '@/pages/site/SiteList.tsx';
import { SspFormAgency } from '@/pages/ssp/SspFormAgency.tsx';
import { SspListAgency } from '@/pages/ssp/SspListAgency.tsx';
import { TrackerEventStatsAgency } from '@/pages/tracker-event-stats/TrackerEventStatsAgency.tsx';
import { TrackerFormAgency } from '@/pages/tracker/TrackerFormAgency.tsx';
import { TrackerListAgency } from '@/pages/tracker/TrackerListAgency.tsx';
import { AgencyTransactionListAgency } from '@/pages/transactions/AgencyTransactionListAgency.tsx';
import TransactionForm from '@/pages/transactions/TransactionForm.tsx';
import { TransactionListAgency } from '@/pages/transactions/TransactionListAgency.tsx';
import { isAuthenticated } from '@/shared/lib/util/Util.tsx';
import { Layout, Spin } from 'antd';
import { AgencyBalanceBean, AgencyBean, ReportRequestParametersObjectObject } from 'common/api/api.ts';
import { agencyBalanceApi } from 'common/api/entitiesApi.tsx'
import { ErrorModal } from 'common/components/ErrorModal.tsx'
import { CommonRoutes, normalizeRoutePath, normalizeSelectedMenuKey } from 'common/context/commonRoutes';
import { useUserContext } from 'common/context/UserContext.tsx';
import { FormMode } from 'common/util/Util.tsx';
import * as React from 'react';
import { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';





const { Content, Header } = Layout

// Loaded on demand: it bundles Monaco editor, which should not be part of the main chunk
const TestRequest = lazy(() => import('@/pages/request/TestRequest.tsx'))
type FeatureAccessEnum = AgencyBean.FeatureAccessEnum

export const AppAgency = () => {
  const [openMenuKeys, setOpenMenuKeys] = React.useState<string[]>([])
  const [selectedMenuKeys, setSelectedMenuKeys] = React.useState<string[]>([])
  const [collapsed, setCollapsed] = useState(false)
  const { user, colorPalette, featureAccess } = useUserContext()
  const [balance, setBalance] = useState<AgencyBalanceBean>()

  const location = useLocation()
  const navigate = useNavigate()
  const fetchBalance = React.useCallback(() => {
    if (!user?.agencyId) return
    agencyBalanceApi.agencyBalance(user.agencyId).then((res) => setBalance(res.data))
  }, [user?.agencyId])

  React.useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login')
      return
    }

    const updateSelectedKeys = () => {
      const path = location.pathname
      const parentKey = getParentMenuKey(path)
      const normalizedSelectedKey = normalizeSelectedMenuKey(path)

      if (parentKey) {
        setOpenMenuKeys((prev) => (prev.includes(parentKey) ? prev : [...prev, parentKey]))
      }
      setSelectedMenuKeys([normalizedSelectedKey])
    }

    updateSelectedKeys()
  }, [location])

  useEffect(() => {
    if (!user?.agencyPublicId) return

    fetchBalance()
    const intervalId = setInterval(fetchBalance, 60_000)

    return () => {
      clearInterval(intervalId)
    }
  }, [user?.agencyPublicId, fetchBalance])

  const getParentMenuKey = (pathName: string) => {
    const selectedKey = normalizeSelectedMenuKey(pathName)
    const found = menuTabs.find((item) =>
      item.children?.some((child) => normalizeRoutePath(child.url) === selectedKey))
    return found ? found.key : ''
  }

  const handleMenuClick = (param: any) => setSelectedMenuKeys([param.key])
  const handleOpenChange = (keys: string[]) => setOpenMenuKeys(keys)
  const reportMenuSelected = (openKey: string, selectedKey: string) => {
    setOpenMenuKeys((prev) => (prev.includes(openKey) ? prev : [...prev, openKey]))
    setSelectedMenuKeys([selectedKey])
  }

  if (!isAuthenticated()) return <Navigate to="/login" />

  const hasFeature = (feature: FeatureAccessEnum) => {
    if (!featureAccess || featureAccess.length === 0) return true
    return featureAccess.includes(feature)
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <SidebarAgency
        openKeys={openMenuKeys}
        selectedKeys={selectedMenuKeys}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        onOpenChange={handleOpenChange}
        handleClick={handleMenuClick}
      />
      <Layout>
        <Header
          style={{
            padding: 0,
            backgroundColor: colorPalette['--color-grey-4'] ?? '#0D1A43',
            display: 'flex',
            alignItems: 'center',
            height: 60,
          }}
        >
          <BreadcrumbAgency collapsed={collapsed} balance={balance} onBalanceRefresh={fetchBalance}
                            stripePaymentsEnabled={balance?.stripePaymentsEnabled ?? false} />
        </Header>
        <ErrorModal />
        <Content style={{ margin: 16 }}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardFormAgency />} />

            <Route
              path={CommonRoutes.REPORT}
              element={<SavedReportListAgency reportType={ReportRequestParametersObjectObject.TypeEnum.Stats} />}
            />
            <Route path={CommonRoutes.REPORT_BUILDER} element={<ReportFormAgency mode={FormMode.create} />} />
            <Route path={CommonRoutes.REPORT_COPY} element={<ReportFormAgency mode={FormMode.copy} />} />
            <Route path={CommonRoutes.REPORT_EDIT} element={<ReportFormAgency mode={FormMode.edit} />} />
            <Route
              path={CommonRoutes.REPORT_OPEN}
              element={<ReportFormAgency mode={FormMode.edit} autoLoad={true} />}
            />

            {hasFeature(AgencyBean.FeatureAccessEnum.Advertiser) && (
              <>
                <Route path="/adv" element={<AdvertiserList menuSelected={reportMenuSelected} />} />
                <Route path="/adv/create" element={<AdvertiserForm mode={FormMode.create} />} />
                <Route path="/adv/:id/edit" element={<AdvertiserForm mode={FormMode.edit} />} />

                <Route path={CommonRoutes.CAMPAIGN} element={<CampaignListAgency />} />
                <Route path={CommonRoutes.CAMPAIGN_CREATE} element={<CampaignFormAgency mode={FormMode.create} />} />
                <Route path={CommonRoutes.CAMPAIGN_EDIT} element={<CampaignFormAgency mode={FormMode.edit} />} />
                <Route path={CommonRoutes.CAMPAIGN_COPY} element={<CampaignFormAgency mode={FormMode.copy} />} />

                <Route path={CommonRoutes.AD_GROUP} element={<AdGroupListAgency />} />
                <Route path="/ad-group/:commonId" element={<AdGroupListAgency />} />
                <Route path={CommonRoutes.AD_GROUP_CREATE} element={<AdGroupFormAgency mode={FormMode.create} />} />
                <Route path={CommonRoutes.AD_GROUP_COPY} element={<AdGroupFormAgency mode={FormMode.copy} />} />
                <Route path={CommonRoutes.AD_GROUP_EDIT} element={<AdGroupFormAgency mode={FormMode.edit} />} />

                <Route path={CommonRoutes.CREATIVE} element={<CreativeListAgency />} />
                <Route path={CommonRoutes.CREATIVE_CREATE} element={<CreativeFormAgency mode={FormMode.create} />} />
                <Route path={CommonRoutes.CREATIVE_COPY} element={<CreativeFormAgency mode={FormMode.copy} />} />
                <Route path={CommonRoutes.CREATIVE_EDIT} element={<CreativeFormAgency mode={FormMode.edit} />} />

                <Route path={CommonRoutes.TRACKER} element={<TrackerListAgency />} />
                <Route path={CommonRoutes.TRACKER_CREATE} element={<TrackerFormAgency mode={FormMode.create} />} />
                <Route path={CommonRoutes.TRACKER_EDIT} element={<TrackerFormAgency mode={FormMode.edit} />} />

                <Route
                  path={CommonRoutes.TRACKER_EVENT_REPORT}
                  element={
                    <SavedReportListAgency
                      reportType={ReportRequestParametersObjectObject.TypeEnum.Tracker}
                      createRoute={CommonRoutes.TRACKER_EVENT_REPORT_CREATE}
                      reportRouteBase={CommonRoutes.TRACKER_EVENT_REPORT}
                      title="Tracker Reports"
                    />
                  }
                />
                <Route path={CommonRoutes.TRACKER_EVENT_REPORT_CREATE} element={<TrackerEventStatsAgency />} />
                <Route
                  path={CommonRoutes.TRACKER_EVENT_REPORT_EDIT}
                  element={
                    <ReportFormAgency
                      mode={FormMode.edit}
                      reportType={ReportRequestParametersObjectObject.TypeEnum.Tracker}
                      savedReportBaseRoute={CommonRoutes.TRACKER_EVENT_REPORT}
                    />
                  }
                />
                <Route
                  path={CommonRoutes.TRACKER_EVENT_REPORT_COPY}
                  element={
                    <ReportFormAgency
                      mode={FormMode.copy}
                      reportType={ReportRequestParametersObjectObject.TypeEnum.Tracker}
                      savedReportBaseRoute={CommonRoutes.TRACKER_EVENT_REPORT}
                    />
                  }
                />

                <Route path={CommonRoutes.FILTER_LIST} element={<FilterListListAgency />} />
                <Route
                  path={CommonRoutes.FILTER_LIST_CREATE}
                  element={<FilterListFormAgency mode={FormMode.create} />}
                />
                <Route path={CommonRoutes.FILTER_LIST_EDIT} element={<FilterListFormAgency mode={FormMode.edit} />} />

                <Route path={CommonRoutes.RETARGETING_LIST} element={<RetargetingListListAgency />} />
                <Route path={CommonRoutes.RETARGETING_LIST_OPEN} element={<RetargetingListFormAgency />} />

                {/*<Route path={CommonRoutes.CONTEXTUAL_TARGETING} element={<ContextualTargetingListAgency />} />*/}

                <Route path={CommonRoutes.FILTER_LIST_GLOBAL} element={<FilterListGlobalListAgency />} />
                <Route
                  path={CommonRoutes.FILTER_LIST_GLOBAL_CREATE}
                  element={<FilterListGlobalFormAgency mode={FormMode.create} />}
                />
                <Route
                  path={CommonRoutes.FILTER_LIST_GLOBAL_EDIT}
                  element={<FilterListGlobalFormAgency mode={FormMode.edit} />}
                />

                <Route path="/agency-transactions" element={<AgencyTransactionListAgency />} />

                <Route path="/transactions" element={<TransactionListAgency />} />
                <Route path="/transactions/create/:id?" element={<TransactionForm />} />

                <Route path="/ad-group-nbr-debug" element={<NbrDebug nbrType="adGroup" />} />
                <Route
                  path="/request"
                  element={
                    <Suspense fallback={<Spin />}>
                      <TestRequest />
                    </Suspense>
                  }
                />
              </>
            )}

            {hasFeature(AgencyBean.FeatureAccessEnum.DSP) && (
              <>
                <Route path="/dsp" element={<DspList />} />
                <Route path="/dsp/create" element={<DspForm mode={FormMode.create} />} />
                <Route path="/dsp/:id/edit" element={<DspForm mode={FormMode.edit} />} />

                <Route path="/dsp-nbr-debug" element={<NbrDebug nbrType="dsp" />} />
              </>
            )}

            {hasFeature(AgencyBean.FeatureAccessEnum.Publisher) && (
              <>
                <Route path="/publishers" element={<PublisherList />} />
                <Route path="/publishers/create" element={<PublisherForm mode={FormMode.create} />} />
                <Route path="/publishers/:id/edit" element={<PublisherForm mode={FormMode.edit} />} />

                <Route path="/sites/:pubId?" element={<SiteList />} />
                <Route path="/sites/create" element={<SiteForm mode={FormMode.create} />} />
                <Route path="/sites/:id/edit" element={<SiteForm mode={FormMode.edit} />} />

                <Route path="/ad-units/:commonId?" element={<AdUnitList />} />
                <Route path="/ad-units/create" element={<AdUnitForm mode={FormMode.create} />} />
                <Route path="/ad-units/:id/edit" element={<AdUnitForm mode={FormMode.edit} />} />

                <Route path="/deals" element={<DealList dealReportSelected={reportMenuSelected} />} />
                <Route path="/deals/create" element={<DealForm mode={FormMode.create} />} />
                <Route path="/deals/:id/edit" element={<DealForm mode={FormMode.edit} />} />
              </>
            )}

            {hasFeature(AgencyBean.FeatureAccessEnum.SSP) && (
              <>
                <Route path="/ssp" element={<SspListAgency />} />
                <Route path="/ssp/create" element={<SspFormAgency mode={FormMode.create} />} />
                <Route path="/ssp/copy/:id" element={<SspFormAgency mode={FormMode.copy} />} />
                <Route path="/ssp/:id/edit" element={<SspFormAgency mode={FormMode.edit} />} />
              </>
            )}

            <Route path="/account" element={<AccountListAgency />} />
            <Route path="/account/create" element={<AccountFormAgency mode={FormMode.create} />} />
            <Route path="/account/:id/edit" element={<AccountFormAgency mode={FormMode.edit} />} />

            <Route path="/logs" element={<LogListAgency />} />
            <Route path="/logs/:entity/:id" element={<LogListAgency />} />
            <Route path="/logs/:entity" element={<LogListAgency />} />

            <Route path="/branding" element={<Branding />} />
            <Route path="/stripe-connect" element={<StripeConnectSettings />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  )
}
